import React from "react";
import { Box, Typography } from "@mui/material";
import CircleArrowRightIcon from "../../../Components/Icons/CircleArrowRightIcon";
import { formatPackChipLabel } from "../recordReplacementHelper";

// Read-only twin of PackReplacementCard: same two-band anatomy and the same colour
// tokens, but label-above-value instead of inputs. Kept as its own component rather
// than a readOnly flag on the form card — the two lay their fields out differently
// enough that one component doing both would be mostly branching.

const BandLabel = ({ text, color }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box
            sx={{
                width: 14,
                height: 10,
                borderRadius: "2px",
                border: `1.5px solid ${color}`,
                flexShrink: 0,
            }}
        />
        <Typography
            sx={{
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                color,
                whiteSpace: "nowrap",
            }}
        >
            {text}
        </Typography>
    </Box>
);

const Field = ({ label, children }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "12px", color: "#8D8D8D", mb: "3px" }}>{label}</Typography>
        <Box
            sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#1A1A1A",
                overflowWrap: "anywhere",
            }}
        >
            {children}
        </Box>
    </Box>
);

const BAND = {
    borderRadius: "10px",
    padding: "14px 18px",
    display: "grid",
    columnGap: "20px",
    rowGap: "12px",
    alignItems: "center",
};

export default function PackSummaryCard({ pack, fields }) {
    return (
        <Box
            sx={{
                border: "1px solid #EAEAEA",
                borderRadius: "12px",
                backgroundColor: "#fff",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    backgroundColor: "#F9F9FC",
                    borderBottom: "1px solid #EFEFEF",
                    padding: "12px 18px",
                }}
            >
                <Box
                    data-testid={`review-pack-chip-${pack?.key}`}
                    sx={{
                        display: "inline-block",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#4A4AF4",
                        backgroundColor: "#EEF0FF",
                        borderRadius: "6px",
                        padding: "5px 12px",
                    }}
                >
                    {formatPackChipLabel(pack)}
                </Box>
            </Box>

            <Box sx={{ padding: "16px 18px 18px" }}>
                <Box
                    sx={{
                        ...BAND,
                        border: "1px solid #ECDCA0",
                        backgroundColor: "#FFFBEA",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "132px minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.7fr)",
                        },
                    }}
                >
                    <BandLabel text="OUTGOING" color="#8A6D0B" />
                    <Field label="Current pack serial number">{fields.outgoingSerial}</Field>
                    <Field label="Installed on">{fields.installedOn}</Field>
                    <Field label="Warranty">{fields.warranty}</Field>

                    {/* Full width beneath the fields: observations are sentences and
                        there can be several, so they do not fit a value column. */}
                    {fields.observations && (
                        <Box sx={{ gridColumn: { md: "1 / -1" } }}>
                            <Typography sx={{ fontSize: "12px", color: "#8D8D8D", mb: "4px" }}>
                                Observation
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                {fields.observations}
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        my: "-14px",
                        position: "relative",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                >
                    <CircleArrowRightIcon direction="down" size={34} />
                </Box>

                <Box
                    sx={{
                        ...BAND,
                        border: "1px solid #BEE0C7",
                        backgroundColor: "#EDF7EF",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "132px minmax(0, 1.7fr) minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.4fr)",
                        },
                    }}
                >
                    <BandLabel text="INCOMING" color="#2E7D32" />
                    <Field label="New pack serial number">{fields.incomingSerial}</Field>
                    <Field label="MFG date">{fields.manufacturingDate}</Field>
                    <Field label="Grade">{fields.grade}</Field>
                    <Field label="Condition">{fields.condition}</Field>
                </Box>
            </Box>
        </Box>
    );
}
